import { CertificateResponse } from '@estercare/ester-shared'
import { appConfig } from '../../../../config'
import { CERTIFICATION_SCREENING_TYPE, CertificationScreening } from './types'

/**
 * Starts the organisation's certification screening, or returns the one already in progress. The
 * backend resolves this per organisation, so an admin can pick up where a colleague left off.
 */
export const postStartCertificationScreening = async ({
  accessToken,
}: {
  accessToken: string
}): Promise<CertificationScreening> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/start`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ type: CERTIFICATION_SCREENING_TYPE }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to start certification screening')
  }
  return await res.json()
}

/**
 * Completes the certification screening and hands it, together with any attached policy documents,
 * to backoffice. Documents can only be attached on this call, so upload before submitting.
 */
export const postSubmitCertificationScreening = async ({
  accessToken,
  screeningId,
  documents,
}: {
  accessToken: string
  screeningId: string
  documents: File[]
}): Promise<CertificationScreening> => {
  const formData = new FormData()
  documents.forEach((document) => formData.append('documents', document))

  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/certificate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to submit certification screening')
  }
  return await res.json()
}

export const getCertification = async ({ accessToken }: { accessToken: string }): Promise<CertificateResponse> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/certificate`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(`Failed to fetch certification: ${res.status}`)
  }
  return res.json()
}
